import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import {
  CreateOfferDocument,
  CreateOfferMutationVariables,
  InfojobsOfferDocument,
  InfojobsOfferPromotionFragment,
  InfojobsOfferValuesFragment,
  OfferFieldsOptionsDocument,
} from 'generated/resources/graphql'
import { omitBy } from 'lodash'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ErrorInformation } from './ErrorInformation'
import { FillInformationSkeleton } from './FillInformationSkeleton'
import { SuboptionSelector } from './SuboptionSelector'
import { INITIAL_ID_VALUE, JobAttributes, checkErrors, mapOptions } from './utils'

interface Props {
  companyId: string
  jobAttributes: JobAttributes
  offer?: InfojobsOfferValuesFragment | undefined
  onSuccess: () => void
  onClose: () => void
}

export const FillInformation = ({ companyId, jobAttributes, offer, onSuccess, onClose }: Props) => {
  const { loading, data } = useQuery(OfferFieldsOptionsDocument, {
    variables: {
      companyId: companyId,
    },
  })
  const [createOffer, { loading: creatingOffer }] = useMutation(CreateOfferDocument, {
    extractorFn: (createdOffer) =>
      createdOffer.integrationsInfojobsMutations.createIntegrationsinfojobsOffer,
  })

  const form = useForm<CreateOfferMutationVariables>({
    defaultValues: {
      atsJobPostingId: jobAttributes.id,
      companyId: companyId,
      contractTypeId: offer?.contractTypeId ?? INITIAL_ID_VALUE,
      countryId: offer?.countryId ?? INITIAL_ID_VALUE,
      desiredSkills: offer?.desiredSkills,
      minimumExperienceId: offer?.minimumExperienceId ?? INITIAL_ID_VALUE,
      minimumStudiesId: offer?.minimumStudiesId ?? INITIAL_ID_VALUE,
      numberOfVacancies: 1,
      provinceId: offer?.provinceId ?? INITIAL_ID_VALUE,
      requiredSkills: offer?.requiredSkills,
      salaryFromId: offer?.salaryFromId ?? INITIAL_ID_VALUE,
      salaryPeriodId: offer?.salaryPerId ?? INITIAL_ID_VALUE,
      salaryToId: offer?.salaryToId ?? INITIAL_ID_VALUE,
      workingDayId: offer?.workingDayId ?? INITIAL_ID_VALUE,
      levelId: offer?.levelId ?? INITIAL_ID_VALUE,
      jobIndustryId: offer?.jobIndustryId ?? INITIAL_ID_VALUE,
      jobSubindustryId: offer?.jobSubindustryId ?? INITIAL_ID_VALUE,
    },
    validate(values) {
      const formErrors: FormErrors<CreateOfferMutationVariables> = {}

      Object.keys(values).forEach((key) => {
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- disabling because Object.keys does not return correct type
        const formKey = key as keyof CreateOfferMutationVariables
        if (values[formKey] === INITIAL_ID_VALUE) {
          formErrors[formKey] = i18n.t('common.validations.empty')
        }
      })

      return formErrors
    },
    onSubmit: async (values, { setErrors }) => {
      const response = await createOffer({
        variables: values,
        refetchQueries: [InfojobsOfferDocument],
      })
      const handled = handleResult(response, (formErrors) => setErrors(formErrors))

      if (handled.success) {
        onSuccess()
      }

      track(trackingEvents.ats.infojobsPromotionFinalized, {
        company_id: companyId,
        success: handled.success,
      })

      return handled
    },
  })

  const errors: string[] = checkErrors(jobAttributes)

  const infojobsData = data?.integrationsInfojobs
  const promotions = infojobsData?.promotions.nodes ?? []

  const mapPromotions = (promotions: InfojobsOfferPromotionFragment[]) => {
    return promotions.map((promotion) => ({
      label: promotion.productName,
      value: promotion.id,
    }))
  }

  const formErrors = omitBy(form.errors, (_value, key) => !Object.keys(form.touched).includes(key))

  if (errors.length > 0) {
    return <ErrorInformation errors={errors} onClose={onClose} />
  }

  return (
    <Modal
      onClose={onClose}
      title={i18n.t('ats.job_details.promotion.infojobs.job.information.title')}
      illustration={'document-edit'}
      description={i18n.t('ats.job_details.promotion.infojobs.job.information.description')}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} type="negative" submit loading={creatingOffer}>
            {i18n.t('common.publish')}
          </Button>
        </Box>
      }
    >
      <Box gap="s16">
        <Callout
          type="info"
          title={i18n.t('ats.job_details.promotion.infojobs.job.information.alert.title')}
          description={i18n.t(
            'ats.job_details.promotion.infojobs.job.information.alert.description'
          )}
        />

        {loading ? (
          <FillInformationSkeleton />
        ) : (
          <Form padding={null}>
            <Select
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.contract_type'
              )}
              options={mapOptions(infojobsData?.contractTypes.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('contractTypeId', value)}
              error={formErrors.contractTypeId}
              value={form.values.contractTypeId}
            />
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.country'
                )}
                options={mapOptions(infojobsData?.countries.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('countryId', value)}
                error={formErrors.countryId}
                value={form.values.countryId}
              />
              <SuboptionSelector
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.province'
                )}
                companyId={companyId}
                category={'province'}
                parentId={form.values.countryId}
                onChange={(value: string) => form.setFieldValue('provinceId', value)}
                suboptionId={form.values.provinceId}
                error={formErrors.provinceId}
              />
            </Box>
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.minimum_studies'
                )}
                options={mapOptions(infojobsData?.studies.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('minimumStudiesId', value)}
                error={formErrors.minimumStudiesId}
                value={form.values.minimumStudiesId}
              />
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.minimum_experience'
                )}
                options={mapOptions(infojobsData?.experience.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('minimumExperienceId', value)}
                error={formErrors.minimumExperienceId}
                value={form.values.minimumExperienceId}
              />
            </Box>
            <Select
              label={i18n.t('ats.job_details.promotion.infojobs.job.information.form_fields.shift')}
              options={mapOptions(infojobsData?.shifts.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('workingDayId', value)}
              error={formErrors.workingDayId}
              value={form.values.workingDayId}
            />
            <Select
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.salary_period'
              )}
              options={mapOptions(infojobsData?.salaryPeriods.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('salaryPeriodId', value)}
              error={formErrors.salaryPeriodId}
              value={form.values.salaryPeriodId}
            />
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.salary_ranges_from'
                )}
                options={mapOptions(infojobsData?.salaryRanges.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('salaryFromId', value)}
                error={formErrors.salaryFromId}
                value={form.values.salaryFromId}
              />
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.salary_ranges_to'
                )}
                options={mapOptions(infojobsData?.salaryRanges.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('salaryToId', value)}
                error={formErrors.salaryToId}
                value={form.values.salaryToId}
              />
            </Box>
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.job_industry'
                )}
                options={mapOptions(infojobsData?.jobIndustry.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('jobIndustryId', value)}
                error={formErrors.jobIndustryId}
                value={form.values.jobIndustryId}
              />
              <SuboptionSelector
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.job_subindustry'
                )}
                companyId={companyId}
                category={'job_subindustry'}
                parentId={form.values.jobIndustryId}
                onChange={(value: string) => form.setFieldValue('jobSubindustryId', value)}
                suboptionId={form.values.jobSubindustryId}
                error={formErrors.jobSubindustryId}
              />
            </Box>
            <Select
              label={i18n.t('ats.job_details.promotion.infojobs.job.information.form_fields.level')}
              options={mapOptions(infojobsData?.level.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('levelId', value)}
              error={formErrors.levelId}
              value={form.values.levelId}
            />
            <TextField
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.required_skills'
              )}
              value={form.values.requiredSkills || ''}
              onChange={(value) => form.setFieldValue('requiredSkills', value)}
            />
            <TextField
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.desired_skills'
              )}
              value={form.values.desiredSkills || ''}
              onChange={(value) => form.setFieldValue('desiredSkills', value)}
            />

            <Selector.Popover
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.promotions'
              )}
              searchPlaceholder={i18n.t('common.search')}
              placeholder={i18n.t('common.select_multiple')}
              blankLabel={i18n.t('common.empty')}
              value={form.values.promotionCodes || []}
              onChange={(value) => form.setFieldValue('promotionCodes', value)}
              isMultiSelect
              hasSearch={false}
              isDisabled={promotions[0]?.id === '0'}
            >
              {mapPromotions(promotions ?? []).map((option) => (
                <Selector.Option
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  searchValue={option.label}
                >
                  {option.label}
                </Selector.Option>
              ))}
            </Selector.Popover>
          </Form>
        )}
      </Box>
    </Modal>
  )
}
